import {ContentContainer, useMessage} from '@coolcolduk/ui-mui';
import {undefinedIfThrow} from '@coolcolduk/util';
import EditIcon from '@mui/icons-material/Edit';
import {Button, Paper, Stack, Typography} from '@mui/material';
import {useCallback, useEffect, useRef, useState} from 'react';
import {useNavigate, useParams} from 'react-router';
import {Question, QuizTimedMode, UserPermission} from '@cp/model';
import {ListQuestionsSingleTopicVariables} from '../../hooks/question/useListQuestions';
import ErrorComponent from '../../components/ErrorComponent/ErrorComponent';
import HintDialog from '../../components/HintDialog';
import MarkdownContentRenderer from '../../atoms/MarkdownContentRenderer/MarkdownContentRenderer';
import BondGraphRenderer from '../../bonds/bondGraphRenderer/BondGraphRenderer';
import ImageDisplayFromStorage from '../../atoms/imageDisplayFromStorage/ImageDisplayFromStorage';
import {PrivateRouteProps} from '../../config/routeType';
import useEndQuizSession from '../../hooks/quizSession/useEndQuizSession';
import useUpdateQuestionAnswer from '../../hooks/question/useUpdateQuestionAnswer';
import useSubscribeQuizSession from '../../hooks/quizSession/useSubscribeQuizSession';
import timeElapse from '../../utils/time/timeElapse';
import LoadingPage from '../other/LoadingPage';
import SessionEffect from './SessionEffect';
import SessionFeedback from './SessionFeedback';
import SessionNavigationHelp from './SessionNavigationHelp';
import SessionPageHeader from './SessionPageHeader';
import SessionQuestionAnswerList from './SessionQuestionAnswerList';
import SessionQuestionHead from './SessionQuestionHead';
import SessionQuestionNavigation from './SessionQuestionNavigation';
import findIndexUnanswerQuestion from './helper/findIndexUnanswerQuestion';
import isSessionQuestionAnswered from './helper/isSessionQuestionAnswered';
import useDataNavigation from '../../hooks/helper/useDataNavigation';
import useSessionTimed from './helper/useSessionTimed';
import useSessionTimedPerQuestion from './helper/useSessionTimedPerQuestion';

export default function SessionPage({user}: PrivateRouteProps) {
  const {sessionId} = useParams<{sessionId: string}>();
  const navigate = useNavigate();
  const {showMessage} = useMessage();

  // functional hooks
  const {updateQuestionAnswer} = useUpdateQuestionAnswer();
  const {endQuizSession} = useEndQuizSession();

  const [selectedAnswer, setSelectedAnswer] = useState<string>('');
  const selectedAnswerRef = useRef<string>('');

  // show state
  const [showFeedback, setShowFeedback] = useState(false);
  const [showHint, setShowHint] = useState(false);

  // Admin check
  const isAdmin = Boolean(user?.roles?.includes(UserPermission.ADMIN));

  // Check if session data was passed from navigation state (from CreateQuizSession)
  const {session, loading: sessionLoading} = useSubscribeQuizSession(sessionId || '');

  // Live session data comes from Firestore subscription
  // Only fall back to passedSessionData if the live session is not yet loaded
  const currentSession = session;

  // data navigation
  const {
    currentData: currentQuestion,
    currentIndex: currentQuestionIndex,
    next: nextQuestion,
    previous: previousQuestion,
    isFirst: isFirstQuestion,
    isLast: isLastQuestion,
  } = useDataNavigation(currentSession?.questions || [], {
    initialIndexFinder: findIndexUnanswerQuestion,
    handleNext: (_current, next) => {
      setSelectedAnswer(next.selectedAnswer || '');
      setShowFeedback(false);
    },
    handlePrevious: (_current, previous) => {
      setSelectedAnswer(previous.selectedAnswer || '');
      setShowFeedback(false);
    },
  });

  // Keep ref in sync with state
  useEffect(() => {
    selectedAnswerRef.current = selectedAnswer;
  }, [selectedAnswer]);

  const processSubmitAnswer = useCallback(() => {
    // if (!selectedAnswer) return; when time is up, the answer will be submitted as empty string
    if (!currentSession || !currentQuestion) return;

    // Get the current selected answer from ref to avoid stale closure
    const currentSelectedAnswer = selectedAnswerRef.current;

    // Show feedback instantly instead of waiting for result
    setShowFeedback(true);

    // Update backend in background (don't await)
    updateQuestionAnswer(currentSession.id, currentQuestion.id, currentSelectedAnswer)
      .then(() => {
        showMessage('Answer updated successfully', 2000, 'success');
      })
      .catch((err: Error) => {
        // Let the error crash the UI
        showMessage('Failed to update answer', 2000, 'error');
        throw err;
      });
    showMessage('Answer updated successfully', 2000, 'success');
  }, [currentSession, currentQuestion, updateQuestionAnswer, showMessage]);

  const handleEndSession = () => {
    undefinedIfThrow(() => {
      if (currentSession?.id && !currentSession.endedAt) endQuizSession(currentSession.id);
    });
    // Navigate to session complete page with sessionId in URL
    navigate(`/session/${sessionId}/complete`);
  };

  // Use the timed per question hook
  const {timeRemaining, startTimer, stopTimer} = useSessionTimedPerQuestion(
    currentSession?.timedMode === QuizTimedMode.TIMED_PER_QUESTION && currentSession.timeDurationSeconds
      ? currentSession.timeDurationSeconds
      : undefined,
    processSubmitAnswer,
  );

  const handleSubmitAnswer = () => {
    processSubmitAnswer();
    stopTimer();
  };

  // Use the timed mode hook
  const {timeRemaining: totalTimeRemaining} = useSessionTimed(
    currentSession?.timedMode === QuizTimedMode.TIMED ? currentSession?.startedAt : undefined,
    currentSession?.timeDurationSeconds || undefined,
    handleEndSession,
  );

  // actions when question changes
  useEffect(() => {
    const answered = isSessionQuestionAnswered(currentQuestion) || showFeedback;
    if (answered) {
      stopTimer();
    } else {
      startTimer(); // Start timer when question is active
    }
  }, [currentQuestion, showFeedback, startTimer, stopTimer]);

  // actions when session ended
  useEffect(() => {
    if (currentSession?.endedAt) {
      const elapsed = timeElapse(currentSession.endedAt);
      // Navigate to session complete page if endedAt is more than 30 seconds ago
      if (elapsed > 30) {
        navigate(`/session/${sessionId}/complete`);
      }
    }
  }, [currentSession?.endedAt, navigate, sessionId]);

  const handleAnswerSelect = (answer: string) => {
    if (showFeedback) return; // Prevent changing answer after submission
    if (currentQuestion?.selectedAnswer) return; // Prevent changing answer if already answered
    setSelectedAnswer(answer);
  };

  const handleEditQuestion = () => {
    if (!currentQuestion || !currentSession) return;

    // Convert SessionQuestion to Question
    const question: Question = {
      id: currentQuestion.id,
      questionText: currentQuestion.questionText,
      hint: currentQuestion.hint,
      options: currentQuestion.options,
      answer: currentQuestion.answer,
      explanation: currentQuestion.explanation,
      moduleTopic: currentQuestion.moduleTopic,
      difficulty: currentQuestion.difficulty,
      status: currentQuestion.status,
      statusNote: currentQuestion.statusNote,
      feedbackPositive: currentQuestion.feedbackPositive ?? 0,
      feedbackNegative: currentQuestion.feedbackNegative ?? 0,
      createdAt: currentQuestion.createdAt,
      randomSeed: currentQuestion.randomSeed,
    };

    // Create filters with moduleId from session
    const filters: ListQuestionsSingleTopicVariables = {
      moduleId: currentSession.moduleId,
      topic: currentQuestion.moduleTopic.topic,
    };

    // Navigate with question and filters in state, and moduleId in URL
    navigate(`/admin/question/${currentQuestion.id}?moduleId=${currentSession.moduleId}`, {
      state: {
        question,
        filters,
        sessionId: currentSession.id, // Pass sessionId so we can navigate back to session
      },
    });
  };

  if (sessionLoading || !currentQuestion) {
    return <LoadingPage minusAppBar />;
  }

  if (!currentSession) {
    return (
      <ErrorComponent message="Session not found" buttonText="Return to Dashboard" handleClick={() => navigate('/')} />
    );
  }

  // Wait for trigger to populate questions: show loading while session has no questions and is not ended
  const hasNoQuestions = !currentSession.questions || currentSession.questions.length === 0;
  if (hasNoQuestions && !currentSession.endedAt) {
    return <LoadingPage minusAppBar />;
  }

  // Session ended but still no questions (trigger failed or no questions in DB)
  if (hasNoQuestions) {
    return (
      <ErrorComponent
        message="Session has no questions"
        buttonText="Return to Dashboard"
        handleClick={() => navigate('/')}
      />
    );
  }

  const progress = ((currentQuestionIndex + 1) / currentSession.questions.length) * 100;
  const isQuestionAnswered = showFeedback || !!currentQuestion?.selectedAnswer;
  const isCorrect = showFeedback ? selectedAnswerRef.current === currentQuestion?.answer : undefined;

  return (
    <ContentContainer data-testid="session-page">
      {/* Header */}
      <SessionPageHeader
        currentQuestionIndex={currentQuestionIndex}
        totalQuestions={currentSession.questions.length}
        progress={progress}
        timedMode={currentSession.timedMode}
        timeRemaining={timeRemaining}
        totalTimeRemaining={totalTimeRemaining}
      />

      {/* Question Card */}
      <Paper sx={{p: 3}}>
        <Stack direction="column" gap={3}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <SessionQuestionHead currentQuestion={currentQuestion} />
            {isAdmin && (
              <Button
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={handleEditQuestion}
                sx={{ml: 2}}
                size="small"
              >
                Edit Question
              </Button>
            )}
          </Stack>

          <ImageDisplayFromStorage imageUrl={currentQuestion?.imageUrl} />
          <BondGraphRenderer graphJsonString={currentQuestion.graphJsonString} />

          <Typography variant="h6" sx={{fontWeight: 500}} data-testid="question-text">
            <MarkdownContentRenderer content={currentQuestion?.questionText || 'Question not available'} />
          </Typography>

          <SessionQuestionAnswerList
            currentQuestion={currentQuestion}
            selectedAnswer={selectedAnswer}
            handleAnswerSelect={handleAnswerSelect}
            disabled={showFeedback || isQuestionAnswered}
          />

          <SessionFeedback
            selectedAnswer={selectedAnswerRef.current}
            currentQuestion={currentQuestion}
            showFeedback={showFeedback || undefined}
          />

          <SessionQuestionNavigation
            currentQuestion={currentQuestion}
            handleHint={() => setShowHint(true)}
            handleNextQuestion={nextQuestion}
            handlePreviousQuestion={previousQuestion}
            handleSubmitAnswer={handleSubmitAnswer}
            handleEndSession={handleEndSession}
            isAnswered={isQuestionAnswered}
            isFirstQuestion={isFirstQuestion}
            isLastQuestion={isLastQuestion}
            session={
              {
                ...currentSession,
                score: Math.round(
                  (currentSession.questions.filter((q) => q.isCorrect).length / currentSession.questions.length) * 100,
                ),
              } as import('@cp/model').QuizSession
            }
          />

          <SessionEffect isCorrect={isCorrect} user={user} />
        </Stack>
      </Paper>

      <SessionNavigationHelp timedMode={currentSession.timedMode} />

      {/* Hint Dialog */}
      <HintDialog
        open={showHint}
        onClose={() => setShowHint(false)}
        hint={currentQuestion?.hint}
        questionText={currentQuestion?.questionText || ''}
        graphJsonString={currentQuestion?.graphJsonString}
      />
    </ContentContainer>
  );
}
